// ═══════════════════════════════════════════════════════════════
// ErrorBoundary — what a mechanic sees instead of a white screen.
//
// A React render error unmounts the whole tree. Before this, that
// meant a blank page with no message, no way back, and nothing
// recorded anywhere the operator could read. On a paying customer's
// phone in a driveway, that's the worst possible failure mode.
//
// Has to be a class component: React only exposes componentDidCatch
// and getDerivedStateFromError to classes, with no hook equivalent.
// ═══════════════════════════════════════════════════════════════
import React from 'react';
import { AlertTriangle, RotateCcw, Home } from 'lucide-react';
import { reportError } from './lib/report-error.js';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    reportError(error, this.props.context || 'react-render', {
      componentStack: String(info?.componentStack || '').slice(0, 4000),
      appVersion: this.props.appVersion || '',
    });
  }

  handleReload = () => {
    // Full reload rather than clearing state: whatever put the tree in
    // this position is likely still in memory.
    window.location.reload();
  };

  handleHome = () => {
    window.location.assign('/');
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="error-shell">
        <div className="error-card">
          <div className="error-icon">
            <AlertTriangle size={30} strokeWidth={2} />
          </div>
          <h1>Something went wrong on this screen</h1>
          <p>
            Your work is saved — this is a display problem, not lost data. Reloading
            usually clears it.
          </p>

          <div className="error-actions">
            <button type="button" className="btn" onClick={this.handleReload}>
              <RotateCcw size={16} strokeWidth={2.5} /> Reload
            </button>
            <button type="button" className="btn-secondary" onClick={this.handleHome}>
              <Home size={16} /> Back to home
            </button>
          </div>

          {/* Shown, not hidden: when they call you, this is the first
              thing you'll ask for. */}
          <details className="error-details">
            <summary>Details for your mechanic's support</summary>
            <p className="error-message mono">
              {this.state.error?.name}: {String(this.state.error?.message || '')}
            </p>
            {this.props.appVersion && (
              <p className="muted small mono">{this.props.appVersion}</p>
            )}
          </details>
        </div>
      </div>
    );
  }
}
